<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>

import { getToken, setToken, setRefreshToken, removeToken, removeRefreshToken } from '@/utils/cookieUtil'
import { systemFetch, accountBanFetch } from '@/api'

// 「这次封禁已经提醒过」的标记，值是 userId:封禁时间，换一次封禁就会再提醒
const BAN_REMINDED_KEY = 'accountBanReminded'

export default {
  name: 'App',

  data () {
    return {
    }
  },

  created () {
    this.getUserInfo()
    
    var that = this

    document.addEventListener('keydown', (event) => {
      if (event.ctrlKey && event.key === 'k') {
        event.preventDefault()
      }
    })

  },

  mounted () {
    // 主题已在 index.html 中提前设置,这里不再需要设置
    // 但保留这段代码以防其他地方有引用
    let theme = sessionStorage.getItem("theme")
    if (theme == null) {
      theme = "light"
    }
    document.documentElement.dataset.theme = theme
    
    // 预渲染环境跳过 API 调用
    if (window.__PRERENDER_INJECTED) {
      return
    }

    // 延迟执行，确保路由完全准备好
    setTimeout(() => {
      this.checkAndLoadData()
    }, 100)
  },

  watch: {
    '$route.path': {
      handler (newPath, oldPath) {
        console.log('Route changed from', oldPath, 'to', newPath)
        // 当路由变化时，检查新路由是否需要跳过 API 调用
        const skipApiCalls = this.$route.meta.skipApiCalls
        
        if (!skipApiCalls) {
          // 不需要跳过，检查是否需要加载数据
          console.log('Route changed to non-privacy page, checking if need to load data...')
          if (!this.$store.state.systemStore.webSiteInfo || !this.$store.state.systemStore.webSiteInfo.name) {
            this.initWebSiteInfo()
          }
          if (!this._reportActiveCalled) {
            this.reportActive()
            this._reportActiveCalled = true
          }
        } else {
          console.log('Route changed to privacy/agreement page, skipping API calls')
        }
      }
    }
  },

  methods: {
    checkAndLoadData () {
      // 从路由 meta 中获取是否需要跳过 API 调用
      const skipApiCalls = this.$route.meta.skipApiCalls
      const currentPath = this.$route.path
      
      console.log('checkAndLoadData - currentPath:', currentPath, 'skipApiCalls:', skipApiCalls)
      
      // 只有不需要跳过时才调用这两个接口
      if (!skipApiCalls) {
        console.log('Loading webSiteInfo and reportActive...')
        // 检查是否已经加载过网站信息，避免重复调用
        if (!this.$store.state.systemStore.webSiteInfo || !this.$store.state.systemStore.webSiteInfo.name) {
          this.initWebSiteInfo()
        }
        // reportActive 只调用一次
        if (!this._reportActiveCalled) {
          this.reportActive()
          this._reportActiveCalled = true
        }
      } else {
        console.log('Skipping API calls for privacy/agreement page')
      }
    },
    initWebSiteInfo () {
      systemFetch.getWebSiteInfo().then(res => {
        this.$store.commit("systemStore/setWebSiteInfo", res.data)
        // this.$store.state.siteAccess = res.extra.siteAccess
        // this.$store.state.visitorAccess = res.extra.visitorAccess
      })
      console.log('this.store.system----->', this.$store.state)
    },
    reportActive () {
      systemFetch.reportActive()
    },
    /**
     * 第三方登录回跳时的封禁处理。
     *
     * 第三方登录走的是 302 回跳，没法像普通接口那样返回 JSON body，所以服务端把封禁的
     * 关键信息拼在 query 上（error=account_banned & banUserId & banPermanent & banReason
     * & banMessage & banEndTime），这里还原成封禁弹窗需要的结构，复用同一个 BanDialog。
     *
     * @returns {boolean} true-本次是封禁回跳，后续登录流程不用再走
     */
    handleBannedRedirect () {
      const search = window.location.search || ''
      if (search.indexOf('error=account_banned') === -1) {
        return false
      }
      const params = new URLSearchParams(search)
      const endTimestamp = Number(params.get('banEndTime'))
      const permanent = params.get('banPermanent') === 'true'

      this.$store.commit('systemStore/showBanDialog', {
        userId: params.get('banUserId'),
        banned: true,
        permanent,
        reasonLabel: params.get('banReason') || '违反社区规范',
        message: params.get('banMessage') || '你的账号已被限制使用。',
        durationText: permanent ? '永久' : '',
        banEndTime: endTimestamp ? this.formatBanEndTime(endTimestamp) : '',
        title: permanent ? '账号已被永久封禁' : '账号已被封禁',
        appealable: true,
        appealTip: '如认为处罚有误，可提交申诉，我们会在 3 个工作日内处理'
      })

      // 清掉地址栏上的封禁参数，避免刷新页面又弹一次
      window.history.replaceState({}, '', window.location.pathname)
      return true
    },
    formatBanEndTime (timestamp) {
      const date = new Date(timestamp)
      const pad = (n) => (n < 10 ? '0' + n : '' + n)
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    getUserInfo () {
      // 第三方登录被封禁拦截：直接展示处罚详情，不再往下走登录流程
      if (this.handleBannedRedirect()) {
        return
      }
      let flag = window.location.href.indexOf("token") != -1
      if (flag) {
        // OAuth 回调登录，先清理旧缓存
        removeToken()
        removeRefreshToken()
        sessionStorage.removeItem('user')
        localStorage.clear()
        
        let token = window.location.href.split("token=")[1].split("&")[0]
        setToken(token)
        // 如果 URL 中包含 refreshToken，也保存它
        let refreshTokenFlag = window.location.href.indexOf("refreshToken") != -1
        if (refreshTokenFlag) {
          let refreshToken = window.location.href.split("refreshToken=")[1].split("&")[0]
          setRefreshToken(refreshToken)
        }
      }

      let token = getToken()
      if (token != null) {
        systemFetch.selectUserInfoByToken(token).then(res => {
          this.userInfo = res.data
          // 如果返回的用户信息中包含 refreshToken，也保存它
          if (res.data && res.data.refreshToken) {
            setRefreshToken(res.data.refreshToken)
          }
          this.$store.commit("userStore/setUserInfo", res.data || {})
          if (res.data && res.data.id) {
            this.remindBanOnce()
          }
        })
      }
    },
    /**
     * 封禁期间账号照常登录、照常浏览，打开网站时提醒一次处罚详情和申诉入口。
     * 同一次封禁在这台设备上只提醒一次；之后发帖、评论等被拦时，request.js 会再弹封禁说明。
     */
    remindBanOnce () {
      accountBanFetch.getBanStatus().then(res => {
        const info = res && res.data
        if (!info) return
        const mark = `${info.userId || ''}:${info.banTime || ''}`
        try {
          if (localStorage.getItem(BAN_REMINDED_KEY) === mark) return
          localStorage.setItem(BAN_REMINDED_KEY, mark)
        } catch (e) {
          // 无痕模式下存储不可用，宁可每次都提醒
        }
        this.$store.commit('systemStore/showBanDialog', info)
      }).catch(() => {
        // 静默：提醒失败不影响使用，受限操作时服务端还会提示
      })
    },
  },

}
</script>


<style scoped lang="scss">
@import "@/assets/styles/common.scss";
</style>